import dayjs from 'dayjs'

import classNames from 'classnames'
import { useRouter } from 'next/router'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'

import {
  getAllSubscriptions,
  useGetAllSubscriptionsQuery,
} from 'common/services/useSubscription'
import SubscriptionTableActions, {
  downloadCSV,
} from './SubscriptionTableActions'
import PlanName from './PlanName'
import { getStore } from 'common/store'
import Loader from 'components/base/Loader'
import { Req } from 'common/types/requests'
import Stats from 'components/account/Stats'
import KYCStatus from 'components/kyc/KYCStatus'
import Pagination from 'components/shared/Pagination'
import Checkbox from 'components/base/forms/Checkbox'
import CancelSubscription from './CancelSubscription'
import { EditIcon, DeleteIcon } from 'components/icons'
import { ErrorMessage } from 'components/base/Messages'
import dateAndTime from 'common/utils/date/dateAndTime'
import { useGetGroupsQuery } from 'common/services/useGroup'
import { SubscriptionSummary } from 'common/types/responses'
import DropdownItem from 'components/base/forms/DropdownItem'
import EllipsisDropdown from 'components/base/EllipsisDropdown'
import { usePermission, useUser } from 'common/services/useUser'
import NoResultsMessage from 'components/tables/NoResultsMessage'
import ContentContainer from 'components/tables/ContentContainer'
import SubscriptionTableFilters from './SubscriptionTableFilters'
import { Permission, SubscriptionStatus } from 'common/types/enums'
import { useGetTeamMembersQuery } from 'common/services/useTeamMember'
import { trackCancelSubscription } from 'common/utils/track-subscription'

type CustomerTableType = {
  groupId?: number
  hideFilter?: boolean
  cta?: (v: SubscriptionSummary) => ReactNode
  filter?: Partial<Req['getAllSubscriptions']>
}

const SubscriptionTable: FC<CustomerTableType> = ({
  cta,
  filter,
  groupId,
  hideFilter,
}) => {
  const router = useRouter()
  const canEdit = usePermission(Permission.WRITE)
  const isGroupUser = usePermission(Permission.GROUP_ONLY)

  const [filters, setFilters] = useState<{
    searchInput: string
    dateFrom?: string
    dateTo?: string
    status: SubscriptionStatus[]
    groups: number[]
    members: number[]
    page: number
  }>({
    groups: [],
    members: [],
    page: 1,
    searchInput: '',
    status: [],
  })

  const [showSelection, setShowSelection] = useState(false)
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<
    SubscriptionSummary[]
  >([])
  const [isExporting, setIsExporting] = useState(false)
  const [activeSubscription, setActiveSubscription] =
    useState<SubscriptionSummary>()
  const [showCancel, setShowCancel] = useState(false)

  const { data: user } = useUser({})
  const { data: allGroups } = useGetGroupsQuery({})
  const { data: allMembers } = useGetTeamMembersQuery({})

  const availableGroups = isGroupUser
    ? user?.groups.map((group) => ({ ...group, id: group.group_id }))
    : allGroups?.results

  const groupsAndMemberGroups = useMemo(() => {
    if (!availableGroups?.length && !allMembers?.results?.length) {
      return filters.groups
    }
    const extraGroupIds = (availableGroups ?? [])
      .filter(
        (item) =>
          !filters.groups.includes(item.id) &&
          'users' in item &&
          item.users?.some((u) => filters.members.includes(u.user_id)),
      )
      .map((item) => item.id)
    return [...filters.groups, ...extraGroupIds]
  }, [availableGroups, allMembers?.results, filters.groups, filters.members])

  const {
    data: subscriptions,
    error,
    isFetching,
    isLoading,
  } = useGetAllSubscriptionsQuery({
    date_from: filters.dateFrom,
    date_to: filters.dateTo,
    groups: groupsAndMemberGroups,
    page: filters.page,
    q: filters.searchInput,
    status: filters.status,
    ...filter,
  })

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubscriptions(subscriptions?.results ?? [])
    } else {
      setSelectedSubscriptions([])
    }
  }

  const handleSelectSubscription =
    (subscription: SubscriptionSummary) => (checked: boolean) => {
      if (checked) {
        setSelectedSubscriptions([...selectedSubscriptions, subscription])
      } else {
        setSelectedSubscriptions(
          selectedSubscriptions.filter((v) => v.id !== subscription.id),
        )
      }
    }

  const handleExportCSV = () => {
    setIsExporting(true)
    getAllSubscriptions(getStore(), {
      date_from: filters.dateFrom,
      date_to: filters.dateTo,
      export: true,
      page: filters.page,
      page_size: subscriptions?.count || 20,
      q: filters.searchInput,
      status: filters.status,
    })
      .then((res) => {
        setIsExporting(false)
        if (res.data) {
          downloadCSV(
            res.data,
            `customers_export_${dayjs().format('YYYY-MM-DD')}.csv`,
          )
        }
      })
      .catch((err) => {
        setIsExporting(false)
        console.error('Failed to export CSV:', err)
      })
  }

  const handlePageChange = (newPage: number) => {
    setFilters({ ...filters, page: newPage })
  }

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters)
  }, [])

  return isLoading && !subscriptions ? (
    <div className='text-center'>
      <Loader />
    </div>
  ) : (
    <>
      {hideFilter !== true && (
        <div key='filter'>
          {canEdit && (
            <div className='d-none d-xl-block'>
              <Stats />
            </div>
          )}
          <SubscriptionTableFilters onFilterChange={handleFilterChange} />
        </div>
      )}
      <SubscriptionTableActions
        groupId={groupId}
        showSelection={showSelection}
        setShowSelection={setShowSelection}
        selectedSubscriptions={selectedSubscriptions}
        setSelectedSubscriptions={setSelectedSubscriptions}
        hasSubscriptionResults={!!subscriptions?.results?.length}
        onExportCSV={handleExportCSV}
        isExporting={isExporting}
        onReassignComplete={() => setFilters({ ...filters, page: 1 })}
      />

      <ErrorMessage className='mb-4'>{error}</ErrorMessage>

      <ContentContainer className='pb-1'>
        <table
          className={classNames({
            disabled: isFetching,
          })}
        >
          <thead>
            <tr style={{ verticalAlign: 'middle' }}>
              {showSelection && (
                <th className='text-nowrap'>
                  <Checkbox
                    id='select-all'
                    onChange={handleSelectAll}
                    checked={
                      selectedSubscriptions.length ===
                      subscriptions?.results?.length
                    }
                  />
                </th>
              )}
              <th className='text-nowrap'>Company</th>
              <th className='d-md-table-cell d-none text-nowrap'>
                Subscription
              </th>
              <th
                style={{ width: 180 }}
                className='d-md-table-cell d-none text-nowrap'
              >
                Date
              </th>
              <th
                style={{ width: 250 }}
                className='d-md-table-cell d-none text-nowrap'
              >
                Group
              </th>
              <th style={{ width: 100 }} className='text-nowrap'>
                Status
              </th>
              <th style={{ width: 50 }} className='text-nowrap'></th>
            </tr>
          </thead>
          <tbody>
            <NoResultsMessage data={subscriptions} />
            {subscriptions?.results?.map((subscription) => {
              const goSubscription = () => {
                router.push(
                  `/customers/${encodeURIComponent(
                    `${subscription.company_id}`,
                  )}/subscriptions/${encodeURIComponent(subscription.id)}`,
                )
              }
              const goCompany = () => {
                router.push(
                  `/customers/${encodeURIComponent(
                    `${subscription.company_id}`,
                  )}`,
                )
              }

              return (
                <tr style={{ verticalAlign: 'middle' }} key={subscription.id}>
                  {showSelection && (
                    <td>
                      <Checkbox
                        id={`select-${subscription.id}`}
                        onChange={handleSelectSubscription(subscription)}
                        checked={selectedSubscriptions.some(
                          (v) => v.id === subscription.id,
                        )}
                      />
                    </td>
                  )}
                  <td
                    onClick={goCompany}
                    className='d-none fw-bold cursor-pointer d-md-table-cell hover-primary'
                  >
                    {subscription.company_name}
                  </td>
                  <td className='d-table-cell d-md-none'>
                    <div
                      onClick={goCompany}
                      className='fw-bold pb-2 hover-primary'
                    >
                      {subscription.company_name}
                    </div>
                    <div
                      onClick={goSubscription}
                      className='fw-semibold hover-primary d-block d-md-none'
                    >
                      {subscription.plan_id && (
                        <PlanName id={subscription.plan_id} />
                      )}
                      <div>{subscription.email_address}</div>
                    </div>
                  </td>
                  <td
                    onClick={goSubscription}
                    className='d-none hover-primary cursor-pointer hover-target d-md-table-cell'
                  >
                    {subscription.plan_id && (
                      <PlanName id={subscription.plan_id} />
                    )}
                  </td>
                  <td className='d-none d-md-table-cell'>
                    {dateAndTime(subscription.created_at)}
                  </td>
                  <td>{subscription?.groups?.map((v) => v.name).join(', ')}</td>
                  <td>
                    <KYCStatus subscription={subscription} />
                  </td>
                  <td>
                    {cta ? (
                      cta(subscription)
                    ) : (
                      <EllipsisDropdown>
                        <DropdownItem
                          icon={<EditIcon className='text-dark300' />}
                          onClick={goSubscription}
                        >
                          Edit Subscription
                        </DropdownItem>
                        <DropdownItem
                          icon={<DeleteIcon className='text-danger' />}
                          onClick={() => {
                            setActiveSubscription(subscription)
                            setShowCancel(true)
                          }}
                        >
                          <span className='text-danger'>
                            Cancel Subscription
                          </span>
                        </DropdownItem>
                      </EllipsisDropdown>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </ContentContainer>

      {!!subscriptions && (
        <Pagination data={subscriptions} onChange={handlePageChange} />
      )}

      {activeSubscription && activeSubscription.company_id != null && (
        <CancelSubscription
          isOpen={showCancel}
          setIsOpen={setShowCancel}
          customerId={activeSubscription.company_id}
          id={activeSubscription.id}
          onCancel={() => trackCancelSubscription(activeSubscription, user!)}
        />
      )}
    </>
  )
}

export default SubscriptionTable
